import {LicenseService} from 'app/core/services/license.service';
import {Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, EventEmitter, Output} from '@angular/core';
import {LinkActions} from 'app/core/components/workflow-new/common/base-card-configuration';
import {CardPresentationComponent} from 'app/core/components/workflow-new/common/card-presentation';
import {MaterialConfiguration} from 'app/core/components/workflow-new/material/configurations/material-configuration';
import {Observable} from 'rxjs/Observable';
import {trigger, style, state, transition, animate} from '@angular/animations';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs/Subject';
import {RequestService} from 'app/core/services/request.service';
import {ConfigService} from 'app/core/services/config.service';

@Component({
  selector: 'itpx-expandable-tile',
  templateUrl: './expandable-tile.component.html',
  styleUrls: ['../../common/style.css', './expandable-tile.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state('open', style({
        opacity: '1',
        display: 'block',
        // transform: 'translate3d(0, 0, 0)'
      })),
      state('closed', style({
        opacity: '0',
        display: 'none',
        // transform: 'translate3d(0, -100%, 0)'
      })),
      transition('closed <=> open', animate('200ms ease-in-out')),
    ]),
    trigger('roundStraight', [
      state('open', style({
        'border-bottom-left-radius': '0px',
        'border-bottom-right-radius': '0px'
      })),
      state('closed', style({
        'border-bottom-left-radius': '4px',
        'border-bottom-right-radius': '4px'
      })),
      transition('closed <=> open', animate('200ms ease-in-out')),
    ])
  ]
})
export class ExpandableTileComponent extends CardPresentationComponent implements OnInit {
  @Input() props$: Observable<any>;
  @Input() conf: MaterialConfiguration;
  @Input() state$: Observable<any>;
  @Input() tileClicked$: Observable<void>;
  @Output() triggerParent = new EventEmitter<any>();
  private sharedState$: Observable<any>;
  private tagType: string;
  private expired: boolean;
  private supplements: string;
  private imageWidth: string;
  private imageHeight: string;
  private cardHeight: string;
  private cardWidth: string;
  private thumbnail: string;
  private readonly downloadableMaterial: string[] = ['ebook', 'ispring', 'archive'];
  private completedCourses: number;
  private completedContents: number;
  private totalCourses: number = 1;
  private openCloseAnim: string;
  private changeText$ = new Subject<any>();
  private properties: any;
  show: boolean[];
  end: number[];
  showEllipsis: boolean[];
  readonly cutoffLimit = 90;
  private markCompleted$ = new Subject<number>();
  private contents: any[];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private detector: ChangeDetectorRef,
    private licenseService: LicenseService,
    private requestService: RequestService,
    private configService: ConfigService
  ) {
    super();
  }

  ngOnInit() {
    this.openCloseAnim = 'closed';
    this.sharedState$ = this.state$.shareReplay(1);
    Object.assign(this, this.conf);
    this.props$.bindToLifecycle(this).subscribe(props => {
      this.completedCourses = props.contents.filter(content => content.coursePercentage === 100).length;
      this.completedContents = props.contents.filter(content => content.completed).length;
      this.totalCourses = props.contents.length;
      this.properties = props;
      this.showEllipsis = this.properties.contents.map(content => content.description && content.description.length > this.cutoffLimit);
      this.show = new Array<boolean>(this.showEllipsis.length).fill(false);
      this.end = this.properties.contents.map(content => Math.min(content.description && content.description.length || 0, this.cutoffLimit));
      if (this.conf.multiContent) {
        this.conf.links = [{
          disabled: false,
          tooltip: null,
          url: [],
          text: 'branded.portal.courses.assignment.showAll',
          type: LinkActions.EXPAND,
        }];
      } else {
        throw new Error('Expecting a multi content deliverable here');
      }
      // this.detector.detectChanges();
    });
    this.sharedState$.bindToLifecycle(this).subscribe(res => {
      this.conf.clickable = res.enabled;
      if (this.downloadableMaterial.includes(res.type.toLowerCase()) && res.googleEmbedLink)
        this.conf.links[0].url = [res.googleEmbedLink];
    });
    this.props$.bindToLifecycle(this).subscribe(res => this.contents = res.contents);
    this.markCompleted$.switchMap(index => {
      let url = this.configService.get('server').url + this.configService.get('endpoints').solar.content.markComplete.api + '/' + this.contents[index].id;
      return Observable.zip(Observable.of(index), this.requestService.post(url, {}).map(_ => _.data)).neverError();
    }).neverError().bindToLifecycle(this).subscribe(([index, res]) => {
      this.contents[index].completed = true;
      this.completedContents = res.completedContentCount;
      this.triggerParent.emit(res);
    });


    this.tileClicked$.bindToLifecycle(this).subscribe(_ => {
      this.onExpandClicked();
      this.detector.detectChanges();
    });
  }

  onExpandClicked() {
    this.openCloseAnim = (this.openCloseAnim == 'open') ? 'closed' : 'open';

    this.conf.links[0].text = (this.openCloseAnim == 'open') ? 'branded.portal.courses.assignment.hideAll' :
      'branded.portal.courses.assignment.showAll';
    this.changeText$.next({});
  }

  trackByFn(index, item) {
    return index; // or item.id
  }

  onClick(event: any, index: number) {
    if (event.target.classList.contains('link') || event.target.parentElement.classList.contains('link')) {
      return;
    }
    if (this.properties.type === 'scormcloud') {
      this.router.navigate([this.properties.type,
        this.properties.contents[index].registrationId, this.properties.contents[index].scormCloudTokenId], {relativeTo: this.route});
    } else if (this.isExternalMaterial(this.properties.type)) {
      this.licenseService.getExternalContentFileUrl(this.properties.contents[index].id)
        .neverError()
        .bindToLifecycle(this)
        .subscribe(response =>
          window.open(response.data, '_blank')
        );
    } else {
      this.router.navigate([this.properties.type, this.properties.contents[index].id], {relativeTo: this.route});
    }
  }

  isExternalMaterial(type: string): boolean {
    return type === 'external-ebook' || type === 'external-podcast'
      || type === 'external-eLearning' || type === 'external-presentation'
      || type === 'external-video' || type === 'external-article' || type === 'external-lab';
  }

  onShowClicked(event: any, i: number) {
    this.show[i] = !this.show[i];
    this.end[i] = this.show[i] ? this.properties.contents[i].description.length : Math.min(this.properties.contents[i].description.length, this.cutoffLimit);
    event.stopPropagation();
  }

  markCompleted(index: number) {
    this.markCompleted$.next(index);
    // console.log(this.properties.contents[index]);
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/workflow-new/material/expandable-tile/expandable-tile.component.ts